import {
    Box,
    Flex,
    Input,
    Center,
    Text,
    Spacer,
    Button,
    InputGroup,
    InputRightElement,
    MenuList,
    useToast,
    MenuItem,
    MenuDivider,
    IconButton,
    Tooltip,
    useBreakpointValue,
    MenuButton,
    Menu,
    Switch,
    CloseButton,
    Image,
    Editable,
    EditableInput,
    EditablePreview,
    useColorModeValue,
    Spinner,
} from "@chakra-ui/react";
import { ColorModeSwitcher } from "../ColorModeSwitcher";
import { useNavigate } from 'react-router-dom';
import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { flushSync } from "react-dom";
import { BsLayoutSidebarInset } from "react-icons/bs";
import { FaClipboard, FaEllipsisV, FaBars, FaFilePdf, FaTrash, FaShareAlt, FaArrowDown } from "react-icons/fa";
import { db, auth } from "../firebase";
import * as Sentry from "@sentry/react";
import { onValue, ref, remove, update, query, orderByChild, equalTo, Unsubscribe, get, set, limitToLast, orderByKey } from "firebase/database";
import fetchSendMessage, { StreamInactivityError } from "../helpers/fetchSendMessage"
import createChatLocal from "../helpers/createChatLocal"
import { providerForMode } from "../helpers/modes"
import getEnvironment from "../helpers/environment"
import UsersModal from "../components/UsersModal"
import { useChatPdf } from '../helpers/useChatPdf';
import { useDropzone } from 'react-dropzone'
import fetchUploadOpenaiFiles from "../helpers/fetchUploadOpenaiFiles";
import fetchSearch from "../helpers/fetchSearch";
import ChatMessages from "../components/ChatMessages";
import Sidebar from "../components/Sidebar";
import DialogueInput from "../components/DialogueInput";
import LegalDraftPanel from "../components/LegalDraftPanel";
import { ChatInterface, FolderInterface } from "../configs";
import { LegalDraftArtifact, LegalDraftSessionState, createLegalDraftArtifact, extractLegalDraft, getLegalDraftDismissKey } from "../helpers/legalArtifacts";


const startButtons = [
    {
        "title": "Get answers to your questions about California workers' compensation law",
        "question": "When does the clock start to accept or deny a claim?"
    },
    {
        "title": "Draft text for a memo, email, or letter",
        "question": "Prepare a memo explaining the time limit for bringing an action for penalties under LC 5814."
    },
    {
        "title": "Request bullet points for a PowerPoint presentation on a topic",
        "question": "Prepare bullet points for a PowerPoint presentation on affirmative defenses."
    },
    {
        "title": "Use it for quick lookups",
        "question": "What is the maximum TTD rate for a 2020 DOI?"
    },
    {
        "title": "Get explanations of medical concepts related to workers compensation",
        "question": "What are the typical physical limitations associated with spondylolisthesis?"
    },
    {
        "title": "Create sample deposition questions",
        "question": "Give me deposition questions to ascertain whether the intoxication defense applies to a slip and fall injury at the annual holiday party."
    },
    {
        "title": "Ask hypothetical questions to learn what factors apply",
        "question": "If someone is injured at a party was sponsored by work but not required to attend, would it be a covered work injury?",
    },
    {
        "title": "Get a summary of cases that speak to a particular issue",
        "question": "List and summarize the key cases that address apportionment to prior nonindustrial injury or condition."
    }
]

const CHATS_PAGE_SIZE = 30;
// An anchored question sits this far below the top of the chat's scroll area (under the 48px fixed top bar).
const CHAT_ANCHOR_TOP = 64;
// Space kept after the last message.
const CHAT_END_SPACE = 30;
const INITIAL_CHAT_MESSAGE_LIMIT = 40;
// A run reports that it is alive every 15 seconds, in every phase. Four missed
// reports mean the server lost the run: keep the chat usable instead of
// spinning.
const STALE_ACTIVE_STATUS_MS = 60 * 1000;
const ACTIVE_STATUS_POLL_MS = 10 * 1000;
const TERMINAL_CHAT_STATUSES = ['completed', 'canceled', 'deleted'];
const DOCUMENT_UPLOAD_ENABLED = false;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

type DialogueFile = { file: File, name: string, id?: string, uploading: boolean };
type IndexedChat = Partial<ChatInterface> & {
    incomingUrl?: string;
    provider?: string;
    usedProfileFeature?: boolean;
    active?: boolean;
};

function isVisibleChat(chat?: IndexedChat | null): boolean {
    return !!chat && chat.status !== 'deleted' && chat.active !== false;
}

function getChatSortTime(chat: IndexedChat): number {
    return chat.lastMessage || chat.time || 0;
}

function getExpiredChatIds(
    entries: [string, IndexedChat][],
    retentionDays: number | null,
    now = Date.now(),
): string[] {
    if (retentionDays === null) return [];

    const cutoff = now - retentionDays * MS_PER_DAY;
    return entries
        .filter(([, chat]) => isVisibleChat(chat))
        .filter(([, chat]) => getChatSortTime(chat) > 0 && getChatSortTime(chat) < cutoff)
        .map(([chatId]) => chatId);
}

function sortChatEntries(entries: [string, IndexedChat][]): [string, IndexedChat][] {
    return entries.sort(([aKey, a], [bKey, b]) => {
        const timeDiff = getChatSortTime(b) - getChatSortTime(a);
        return timeDiff !== 0 ? timeDiff : bKey.localeCompare(aKey);
    });
}

// A chat filed in a folder that still exists. It belongs in the sidebar however old it is: the folder is built from the
// loaded chats, so a filed chat outside the loaded pages would leave its folder looking empty.
function isFiledChat(chat: IndexedChat, folders: Record<string, FolderInterface>): boolean {
    return !!chat.folderId && !!folders[chat.folderId];
}

function filterVisibleChatEntries(entries: [string, IndexedChat][]): [string, IndexedChat][] {
    return entries.filter(([, chat]) => isVisibleChat(chat));
}

function normalizeMessages(value: any): any[] {
    if (!value) return [];
    if (Array.isArray(value)) {
        return value
            .map((message, index) => {
                if (!message) return null;
                return typeof message === 'object'
                    ? { ...message, _rawIndex: index }
                    : message;
            })
            .filter(Boolean);
    }

    return Object.entries(value)
        .sort(([aKey], [bKey]) => Number(aKey) - Number(bKey))
        .map(([key, message]) => {
            if (!message || typeof message !== 'object') return message;
            const rawIndex = Number(key);
            return Number.isNaN(rawIndex)
                ? message
                : { ...(message as Record<string, any>), _rawIndex: rawIndex };
        })
        .filter(Boolean);
}

// The live listener sees only the last INITIAL_CHAT_MESSAGE_LIMIT messages. Keep
// the earlier history already loaded instead of dropping it: a chat only changes
// at its end, so every message before the tail's first index still holds.
function mergeMessageTail(history: any[], tail: any[]): any[] {
    const firstTailIndex = tail[0]?._rawIndex;
    if (typeof firstTailIndex !== 'number' || firstTailIndex === 0) return tail;
    const head = history.filter((message: any) =>
        typeof message?._rawIndex === 'number' && message._rawIndex < firstTailIndex
    );
    return [...head, ...tail];
}

// True when the loaded messages do not start at the first stored message.
function isPartialHistory(messages: any[]): boolean {
    const first = messages.find((message: any) => typeof message?._rawIndex === 'number');
    return !!first && first._rawIndex !== 0;
}

// Next free index in rawChatMessages, so appends do not rewrite the stored chat.
function nextRawIndex(messages: any[]): number {
    return messages.reduce((next: number, message: any) =>
        typeof message?._rawIndex === 'number' ? Math.max(next, message._rawIndex + 1) : next, 0);
}

function getMessageText(message: any): string {
    const content = message?.content;
    if (typeof content === 'string') return content;
    if (!Array.isArray(content)) return '';

    return content
        .map((part: any) => typeof part === 'string' ? part : part?.text || '')
        .filter(Boolean)
        .join(' ');
}

function getDefaultApiAddress(): string {
    const explicitAddress = process.env.REACT_APP_API_ADDRESS?.trim();
    const normalizeAddress = (address: string) => (
        address.startsWith('http://') || address.startsWith('https://')
            ? address
            : `https://${address}`
    );

    if (process.env.REACT_APP_CURRENT_ENV === 'production') {
        return explicitAddress ? normalizeAddress(explicitAddress) : 'https://chatsocapi.sullivanoncomp.com';
    }

    if (process.env.REACT_APP_CURRENT_ENV === 'local') {
        const localAddress = explicitAddress && /^(https?:\/\/)?(localhost|127\.0\.0\.1)(:\d+)?/i.test(explicitAddress)
            ? explicitAddress
            : 'http://localhost:8000';
        return normalizeAddress(localAddress);
    }

    return explicitAddress ? normalizeAddress(explicitAddress) : 'https://staging-chatsocapi.sullivanoncomp.com';
}

export default function ChatPage({
    useAutocomplete,
    canLinkCaseCitations,
    useUserProfile,
    companyChatRetentionDays,
}: {
    useAutocomplete: boolean;
    canLinkCaseCitations: boolean;
    useUserProfile: boolean;
    companyChatRetentionDays: number | null;
}) {
    const [dialogueBox, setDialogueBox] = useState<string>('');
    const [currentChat, setCurrentChat] = useState<string>(() => new URLSearchParams(window.location.search).get('id') || '');
    const [waitAnswer, setWaitAnswer] = useState<boolean>(false);
    const [loadingChat, setLoadingChat] = useState<boolean>(false);
    const [newDialogueReady, setNewDialogueReady] = useState<boolean>(false);
    const [allChats, setAllChats] = useState<any>({});
    const [folders, setFolders] = useState<Record<string, FolderInterface>>({});
    const [chatMessages, setChatMessages] = useState<any[]>([]);
    const [showBar, setShowBar] = useState<boolean>(true);
    const [windowAddUser, setWindowAddUser] = useState<boolean>(false);
    const [authorizedUsers, setAuthorizedUsers] = useState<string[]>([]);
    const [currentChatShared, setCurrentChatShared] = useState<boolean>(false);
    const [dialogueFiles, setDialogueFiles] = useState<DialogueFile[]>([]);
    const [searchText, setSearchText] = useState<string>('');
    const [apiAddress, setApiAddress] = useState<string>(getDefaultApiAddress);
    const [matchedChats, setMatchedChats] = useState<string[]>([]);
    const [loadingSearch, setLoadingSearch] = useState<boolean>(false);
    const [sentTime, setSentTime] = useState<number[]>([]);
    const [activeLegalDraft, setActiveLegalDraft] = useState<LegalDraftArtifact | null>(null);
    const [legalDraftStates, setLegalDraftStates] = useState<Record<string, LegalDraftSessionState>>({});
    const [legalDraftStatesLoading, setLegalDraftStatesLoading] = useState<boolean>(false);
    const [sourceVisibilityByMessage, setSourceVisibilityByMessage] = useState<Record<number, boolean>>({});

    const [responseMode, setResponseMode] = useState<string>('fast');
    const [hasMoreChats, setHasMoreChats] = useState<boolean>(true);
    const [loadingMoreChats, setLoadingMoreChats] = useState<boolean>(false);
    const [initialChatHistoryLoaded, setInitialChatHistoryLoaded] = useState<boolean>(false);
    const [pendingFullHistoryUid, setPendingFullHistoryUid] = useState<string>('');
    const isPartialChatHistory = useMemo(() => isPartialHistory(chatMessages), [chatMessages]);
    const isDesktop = useBreakpointValue<boolean>({ base: false, md: true });
    const scrollableDivRef = useRef<HTMLDivElement>(null);
    const chatScrollContainerRef = useRef<HTMLDivElement>(null);
    // Streaming answers do not pull the view down. A question sent from here is scrolled once to the top of the
    // view and its answer grows below it; the reader scrolls on when ready ("Latest" jumps to the end).
    const anchorNextQuestionRef = useRef(false);
    const anchoredTurnRef = useRef(false);
    const openedChatRef = useRef('');
    const seenChatRef = useRef('');
    const atLatestRef = useRef(true);
    // Where the anchored question last sat in the view, so content changing above it cannot move the reader.
    const questionInViewRef = useRef<{ text: string; top: number } | null>(null);
    const anchorScrollUntilRef = useRef(0);
    const [showJumpToLatest, setShowJumpToLatest] = useState(false);
    const chatHistoryLimitRef = useRef(CHATS_PAGE_SIZE);
    const fullChatEntriesRef = useRef<[string, IndexedChat][]>([]);
    const foldersRef = useRef<Record<string, FolderInterface>>({});
    const fullChatHistoryPromiseRef = useRef<Promise<[string, IndexedChat][]> | null>(null);
    const fullChatHistoryLoadedRef = useRef(false);
    const retentionPolicyAppliedForDaysRef = useRef<number | null>(null);
    const chatMessagesCacheRef = useRef<Record<string, any[]>>({});
    const prefetchingChatIdsRef = useRef<Set<string>>(new Set());
    const sendingChatsRef = useRef(new Set<string>());
    const streamingChatsRef = useRef(new Set<string>());
    const currentChatIdRef = useRef<string>('');
    const dismissedLegalDraftIdsRef = useRef<Set<string>>(new Set());
    const dismissedLegalDraftKeysRef = useRef<Set<string>>(new Set());
    const suppressLegalDraftAutoOpenForChatRef = useRef<string | null>(null);
    const didNormalizeInitialApiAddressRef = useRef(false);
    const toast = useToast();
    const navigate = useNavigate();
    const { downloadPdf, isGenerating: isGeneratingPdf } = useChatPdf('chatsoc_conv.pdf');

    // Memoize the references array to prevent ChatMessages re-renders
    const currentRefs = allChats[currentChat]?.references;
    const memoizedReferences = useMemo(() => currentRefs || [], [currentRefs]);

    useEffect(() => {
        if (didNormalizeInitialApiAddressRef.current) return;
        didNormalizeInitialApiAddressRef.current = true;

        if (
            process.env.REACT_APP_CURRENT_ENV === 'local' &&
            apiAddress === 'https://staging-chatsocapi.sullivanoncomp.com'
        ) {
            setApiAddress(getDefaultApiAddress());
        }
    }, [apiAddress]);

    useEffect(() => {
        currentChatIdRef.current = currentChat;
    }, [currentChat]);

    useEffect(() => {
        setActiveLegalDraft(null);
        setLegalDraftStates({});
        setLegalDraftStatesLoading(!!currentChat);
        setSourceVisibilityByMessage({});
        dismissedLegalDraftIdsRef.current.clear();
        dismissedLegalDraftKeysRef.current.clear();
        suppressLegalDraftAutoOpenForChatRef.current = currentChat || null;
    }, [currentChat]);

    useEffect(() => {
        if (!currentChat) {
            setLegalDraftStates({});
            setLegalDraftStatesLoading(false);
            return;
        }

        setLegalDraftStatesLoading(true);
        const legalDraftStatesRef = ref(db, `legalDraftSessions/${currentChat}`);
        const unsubscribeLegalDraftStates = onValue(legalDraftStatesRef, (snapshot) => {
            setLegalDraftStates(snapshot.val() || {});
            setLegalDraftStatesLoading(false);
        }, (error) => {
            console.error('Could not load legal draft state:', error);
            setLegalDraftStatesLoading(false);
        });

        return unsubscribeLegalDraftStates;
    }, [currentChat]);

    useEffect(() => {
        if (!activeLegalDraft) return;

        const message = chatMessages[activeLegalDraft.messageIndex];
        const text = message?.role === 'assistant' ? message.content?.[0]?.text || '' : '';
        const updatedArtifact = createLegalDraftArtifact(text, activeLegalDraft.messageIndex);
        if (
            updatedArtifact
            && (
                updatedArtifact.text !== activeLegalDraft.text
                || updatedArtifact.title !== activeLegalDraft.title
                || updatedArtifact.kind !== activeLegalDraft.kind
            )
        ) {
            setActiveLegalDraft({
                ...updatedArtifact,
                id: activeLegalDraft.id,
            });
        }
    }, [activeLegalDraft, chatMessages]);

    useEffect(() => {
        if (!currentChat || activeLegalDraft) return;
        if (suppressLegalDraftAutoOpenForChatRef.current === currentChat) return;

        for (let idx = chatMessages.length - 1; idx >= 0; idx -= 1) {
            const message = chatMessages[idx];
            const text = message?.role === 'assistant' ? message.content?.[0]?.text || '' : '';
            const artifact = createLegalDraftArtifact(text, idx);
            if (
                artifact
                && !dismissedLegalDraftIdsRef.current.has(artifact.id)
                && !dismissedLegalDraftKeysRef.current.has(getLegalDraftDismissKey(artifact))
            ) {
                setShowBar(false);
                setActiveLegalDraft(artifact);
                return;
            }
        }
    }, [activeLegalDraft, chatMessages, currentChat]);

    // Dark mode colors
    const mainBg = useColorModeValue("white", "gray.800");
    const topBarBg = useColorModeValue("white", "gray.800");
    const borderColor = useColorModeValue("gray.200", "gray.700");
    const jumpButtonBg = useColorModeValue("white", "gray.700");
    const jumpButtonHoverBg = useColorModeValue("gray.50", "gray.600");
    const jumpButtonColor = useColorModeValue("#17404e", "gray.100");
    const textColor = useColorModeValue("#17404e", "white");
    const secondaryTextColor = useColorModeValue("gray", "gray.400");
    const startButtonHover = useColorModeValue("gray.50", "gray.700");
    const menuBg = useColorModeValue("white", "gray.700");
    const menuHover = useColorModeValue("gray.50", "gray.600");
    const menuText = useColorModeValue("gray.700", "gray.200");
    const deleteHover = useColorModeValue("red.50", "rgba(254, 226, 226, 0.1)");
    const inputBg = useColorModeValue("gray.50", "gray.700");
    const iconColor = useColorModeValue("gray.600", "gray.400");
    const iconHover = useColorModeValue("gray.100", "gray.700");

    const acceptedFormats = useMemo(() => [
        ".doc",
        ".docx",
        ".html",
        ".json",
        ".pdf",
        ".md",
        ".txt",
        ".pptx",
    ], []);

    const startFileUpload = useCallback((files: File[]) => {
        const newEntries = files.map(f => ({ file: f, name: f.name, uploading: true }));
        setDialogueFiles(prev => {
            const combined = [...prev, ...newEntries];
            return combined.slice(0, 5); // max 5
        });
        // Eagerly upload each file
        for (const entry of newEntries) {
            fetchUploadOpenaiFiles(entry.file, apiAddress)
                .then(result => {
                    setDialogueFiles(prev =>
                        prev.map(df =>
                            df.file === entry.file ? { ...df, id: result.id, uploading: false } : df
                        )
                    );
                })
                .catch(() => {
                    setDialogueFiles(prev => prev.filter(df => df.file !== entry.file));
                    toast({
                        position: 'top',
                        title: 'Upload failed',
                        description: `Could not upload ${entry.name}`,
                        status: 'error',
                        duration: 4000,
                        isClosable: true,
                    });
                });
        }
    }, [apiAddress, toast]);

    const onDrop = useCallback((acceptedFiles: File[]) => {
        const validFiles: File[] = [];
        const invalidFiles: string[] = [];

        for (const file of acceptedFiles) {
            const fileExtension = file.name.split('.').pop()?.toLowerCase();

            if (!acceptedFormats.includes(`.${fileExtension}`)) {
                invalidFiles.push(file.name);
            } else {
                validFiles.push(file);
            }
        }

        if (invalidFiles.length > 0) {
            toast({
                position: 'top',
                title: 'Invalid file format',
                description: `Unsupported files skipped: ${invalidFiles.join(', ')}. Accepted formats: .doc, .docx, .html, .json, .md, .pdf, .pptx, .txt`,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }

        if (validFiles.length > 0) {
            startFileUpload(validFiles);
        }
    }, [acceptedFormats, toast, startFileUpload])
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        noClick: true,
        disabled: !DOCUMENT_UPLOAD_ENABLED,
    })

    const handleDialogueBoxChange = useCallback((value: string) => {
        setDialogueBox(value);
    }, []);

    const handleResponseModeChange = useCallback((mode: string) => {
        setResponseMode(mode);
    }, []);

    const openLegalDraft = useCallback((artifact: LegalDraftArtifact) => {
        dismissedLegalDraftIdsRef.current.delete(artifact.id);
        dismissedLegalDraftKeysRef.current.delete(getLegalDraftDismissKey(artifact));
        suppressLegalDraftAutoOpenForChatRef.current = null;
        setShowBar(false);
        setActiveLegalDraft(artifact);
    }, []);

    const handleSourceVisibilityChange = useCallback((messageIndex: number, showSources: boolean) => {
        setSourceVisibilityByMessage(prev => ({
            ...prev,
            [messageIndex]: showSources,
        }));
    }, []);

    const closeLegalDraft = useCallback(() => {
        suppressLegalDraftAutoOpenForChatRef.current = currentChat || null;
        setActiveLegalDraft(prev => {
            if (prev) {
                dismissedLegalDraftIdsRef.current.add(prev.id);
                dismissedLegalDraftKeysRef.current.add(getLegalDraftDismissKey(prev));
            }
            return null;
        });
    }, [currentChat]);

    const addLegalDraftChatResponse = useCallback((question: string, answer: string) => {
        if (!currentChat) return;
        const now = Date.now();
        setChatMessages((prev: any) => {
            // Append at the stored indexes: prev may hold only the tail of the chat,
            // and writing it whole would delete the earlier messages.
            const index = nextRawIndex(prev);
            const userMessage = {
                role: 'user',
                content: [{ type: 'text', text: question }],
                time: now,
            };
            const assistantMessage = {
                role: 'assistant',
                content: [{ type: 'text', text: answer }],
                time: now,
                duration: 0,
            };
            update(ref(db, 'rawChatMessages/' + currentChat), {
                [index]: userMessage,
                [index + 1]: assistantMessage,
            });
            return [
                ...prev,
                { ...userMessage, _rawIndex: index },
                { ...assistantMessage, _rawIndex: index + 1 },
            ];
        });
    }, [currentChat]);

    const handlePersistLegalDraftState = useCallback((artifactId: string, state: LegalDraftSessionState) => {
        if (!currentChat || !artifactId) return;

        const nextState = {
            ...state,
            updatedAt: state.updatedAt || Date.now(),
        };

        setLegalDraftStates(prev => ({
            ...prev,
            [artifactId]: nextState,
        }));

        set(ref(db, `legalDraftSessions/${currentChat}/${artifactId}`), nextState)
            .catch((error) => {
                console.error('Could not save legal draft state:', error);
            });
    }, [currentChat]);

    const handleUpdateArtifact = useCallback(async (newText: string) => {
        if (!activeLegalDraft || !currentChat) return;

        const msgIdx = activeLegalDraft.messageIndex;
        const updatedMessages = [...chatMessages];
        const message = updatedMessages[msgIdx];
        if (message && message.role === 'assistant' && message.content?.[0]) {
            const originalText = message.content[0].text || '';
            const taggedDraft = extractLegalDraft(originalText);
            let nextText = originalText;
            if (taggedDraft) {
                const openTagMatch = originalText.match(/<(artifact|letter|memo|memorandum|draft|legal-draft|legal_draft|document)\b([^>]*)>/i);
                if (openTagMatch && openTagMatch.index !== undefined) {
                    const tagName = openTagMatch[1];
                    const closeTagPattern = new RegExp(`</${tagName.replace('-', '[-_]')}>`, 'i');
                    const closeTagMatch = originalText.slice(openTagMatch.index + openTagMatch[0].length).match(closeTagPattern);
                    if (closeTagMatch && closeTagMatch.index !== undefined) {
                        const contentStart = openTagMatch.index + openTagMatch[0].length;
                        const contentEnd = contentStart + closeTagMatch.index;
                        nextText = originalText.slice(0, contentStart) + `\n${newText}\n` + originalText.slice(contentEnd);
                    } else {
                        nextText = originalText.slice(0, openTagMatch.index + openTagMatch[0].length) + `\n${newText}\n</${tagName}>`;
                    }
                } else {
                    nextText = originalText.replace(taggedDraft.text, newText);
                }
            } else {
                nextText = newText;
            }

            message.content[0].text = nextText;
            setChatMessages(updatedMessages);
            // Write only the edited text: chatMessages may hold only the tail of the chat.
            await set(ref(db, `rawChatMessages/${currentChat}/${message._rawIndex ?? msgIdx}/content/0/text`), nextText);
            
            // Also update activeLegalDraft to keep it in sync
            const nextArtifact = createLegalDraftArtifact(nextText, msgIdx);
            setActiveLegalDraft(prev => prev
                ? { ...(nextArtifact || prev), id: prev.id, text: newText }
                : null);
        }
    }, [activeLegalDraft, chatMessages, currentChat]);

    useEffect(() => {
        // Minimum 2 characters to search
        if (searchText.length < 2) {
            setMatchedChats([]);
            setLoadingSearch(false);
            return;
        }

        // Instant title search (from memory, no API call)
        const titleMatches = allChats
            ? Object.keys(allChats).filter((x) =>
                isVisibleChat(allChats[x]) &&
                (allChats[x].title || '').toLowerCase().includes(searchText.toLowerCase())
            )
            : [];
        setMatchedChats(titleMatches);
        setLoadingSearch(true);

        // Debounced message search with AbortController
        const abortController = new AbortController();
        const timeoutId = setTimeout(() => {
            const uid = auth.currentUser?.uid;
            if (!uid) {
                setLoadingSearch(false);
                return;
            }
            fetchSearch(searchText, uid, apiAddress, abortController.signal)
                .then((results: Array<{ id: string, title: string, lastMessage: number, status?: string, active?: boolean }>) => {
                    const visibleResults = results.filter((result) => isVisibleChat(result));
                    // Extract IDs for matchedChats
                    const ids = visibleResults.map(r => r.id);
                    setMatchedChats(ids);

                    // Merge search results into allChats so titles display correctly
                    setAllChats((prev: any) => {
                        const merged = { ...prev };
                        visibleResults.forEach(r => {
                            // Keep folder membership and anything else already known.
                            merged[r.id] = {
                                ...(prev[r.id] || {}),
                                title: r.title,
                                lastMessage: r.lastMessage,
                                status: r.status,
                                active: r.active
                            };
                        });
                        return merged;
                    });

                    setLoadingSearch(false);
                })
                .catch((err) => {
                    // Ignore abort errors (expected when user types again)
                    if (err.name !== 'AbortError') {
                        console.error('Search error:', err);
                        setLoadingSearch(false);
                    }
                });
        }, 600); // 600ms debounce

        return () => {
            clearTimeout(timeoutId);
            abortController.abort(); // Cancel any in-flight request
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchText, apiAddress]); // Removed allChats to prevent re-triggering


    useEffect(() => {
        if (newDialogueReady && currentChat) {
            sendMessage();
            setNewDialogueReady(false);
        }
    }, [newDialogueReady, currentChat]);

    useEffect(() => {

        if (currentChat) {
            const cachedMessages = chatMessagesCacheRef.current[currentChat];
            if (cachedMessages) {
                setChatMessages(cachedMessages);
                setLoadingChat(false);
            } else {
                setChatMessages([]);
                setLoadingChat(true);
            }
            navigate(`/chat?id=${currentChat}`);
            const unsubscriptionFunctions: Unsubscribe[] = [];
            let isActiveChatSubscription = true;
            let didStartFullHistoryHydration = false;
            let didScheduleFullHistoryHydration = false;
            let fullHistoryIdleHandle: number | null = null;
            let fullHistoryTimeoutHandle: number | null = null;
            const idleWindow = window as typeof window & {
                requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
                cancelIdleCallback?: (handle: number) => void;
            };

            const hydrateFullCurrentChat = () => {
                if (didStartFullHistoryHydration) return;
                didStartFullHistoryHydration = true;

                get(ref(db, 'rawChatMessages/' + currentChat))
                    .then((snapshot) => {
                        if (!isActiveChatSubscription) return;
                        const data = normalizeMessages(snapshot.val());
                        chatMessagesCacheRef.current[currentChat] = data;
                        setChatMessages(data);
                    })
                    .catch((error) => {
                        if (isActiveChatSubscription) {
                            console.error("Error hydrating full current chat history:", error);
                        }
                    });
            };

            const scheduleFullCurrentChatHydration = () => {
                if (didScheduleFullHistoryHydration) return;
                didScheduleFullHistoryHydration = true;

                if (idleWindow.requestIdleCallback) {
                    fullHistoryIdleHandle = idleWindow.requestIdleCallback(hydrateFullCurrentChat, { timeout: 10000 });
                } else {
                    fullHistoryTimeoutHandle = window.setTimeout(hydrateFullCurrentChat, 1500);
                }
            };

            // Subscribe to current chat's metadata for real-time title/status updates
            const currentChatRef = ref(db, 'chats/' + currentChat);
            const unsubscribeCurrentChat = onValue(currentChatRef, (snapshot) => {
                if (snapshot.val()) {
                    const chatData = snapshot.val() as ChatInterface;
                    if (!isVisibleChat(chatData)) {
                        setAllChats((prev: any) => {
                            const updated = { ...prev };
                            delete updated[currentChat];
                            return updated;
                        });
                        setCurrentChat('');
                        return;
                    }
                    // Update this chat in allChats state
                    setAllChats((prev: any) => ({
                        ...prev,
                        [currentChat]: chatData
                    }));
                }
            });
            unsubscriptionFunctions.push(unsubscribeCurrentChat);


            const chatMessagesRef = query(
                ref(db, 'rawChatMessages/' + currentChat),
                orderByKey(),
                limitToLast(INITIAL_CHAT_MESSAGE_LIMIT)
            );
            const unsubscribeChatMessages = onValue(chatMessagesRef, (snapshot) => {
                if (snapshot.val()) {
                    const tail = normalizeMessages(snapshot.val());

                    setChatMessages((prevMessages: any) => {
                        const data = mergeMessageTail(prevMessages, tail);
                        if (!isPartialHistory(data)) {
                            chatMessagesCacheRef.current[currentChat] = data;
                        }

                        // Check if we have optimistic messages
                        const hasOptimisticAssistant = prevMessages.some((m: any) => m._loading);

                        // While this tab owns the API stream, keep the streamed text as the
                        // visible source of truth. Final Firebase snapshots include hidden
                        // tool messages that can move the assistant answer to another index.
                        if (streamingChatsRef.current.has(currentChat) && hasOptimisticAssistant) {
                            return prevMessages;
                        }

                        // If we have an optimistic assistant loading placeholder
                        // and the new data doesn't have an assistant response yet
                        if (hasOptimisticAssistant && data.length < prevMessages.length) {
                            // Keep the loading assistant placeholder
                            return [...data, prevMessages[prevMessages.length - 1]];
                        }

                        // Otherwise, replace with real data (earlier history kept)
                        return data;
                    });
                } else {
                    chatMessagesCacheRef.current[currentChat] = [];
                    setChatMessages([]);
                }
                setLoadingChat(false);
                scheduleFullCurrentChatHydration();
            });
            unsubscriptionFunctions.push(unsubscribeChatMessages);
            return () => {
                isActiveChatSubscription = false;
                if (fullHistoryIdleHandle !== null && idleWindow.cancelIdleCallback) {
                    idleWindow.cancelIdleCallback(fullHistoryIdleHandle);
                }
                if (fullHistoryTimeoutHandle !== null) {
                    window.clearTimeout(fullHistoryTimeoutHandle);
                }
                unsubscriptionFunctions.forEach((unsubscribe) => {
                    unsubscribe();
                });
            };
        } else {
            navigate('/chat');
            setChatMessages([]);
            // No subscription in this branch, so no cleanup function is needed
        }
    }, [currentChat]);


    const appendChatEntries = useCallback((entries: [string, IndexedChat][], replace = false) => {
        const chats = Object.fromEntries(filterVisibleChatEntries(entries));
        setAllChats((prev: any) => {
            if (!replace) return { ...prev, ...chats };

            // The full history is read once per session. The loaded copies carry what changed since (new messages,
            // titles, folder moves) and the chats made since, and search results and the open chat can be older
            // than the loaded pages: keep them all over the snapshot.
            return Object.fromEntries(sortChatEntries(Object.entries({ ...chats, ...prev })));
        });
    }, []);

    const showChatEntries = useCallback((entries: [string, IndexedChat][]) => {
        const activeEntries = filterVisibleChatEntries(entries);
        const visibleEntries = activeEntries.slice(0, chatHistoryLimitRef.current);
        const shown = new Set(visibleEntries.map(([chatId]) => chatId));
        const filedEntries = activeEntries.filter(([chatId, chat]) => !shown.has(chatId) && isFiledChat(chat, foldersRef.current));
        appendChatEntries([...visibleEntries, ...filedEntries], true);
        setHasMoreChats(chatHistoryLimitRef.current < activeEntries.length);
        return visibleEntries.length > 0;
    }, [appendChatEntries]);

    const hydrateFullChatHistory = useCallback((uid: string): Promise<[string, IndexedChat][]> => {
        // Once per session: every chat's metadata grows with the history, and Firebase answers in order, so a
        // repeated read holds up the next chat being opened.
        if (fullChatHistoryLoadedRef.current) {
            return Promise.resolve(fullChatEntriesRef.current);
        }
        if (fullChatHistoryPromiseRef.current) {
            return fullChatHistoryPromiseRef.current;
        }

        fullChatHistoryPromiseRef.current = get(query(ref(db, 'chats/'), orderByChild("userId"), equalTo(uid)))
            .then((snapshot) => {
                if (!snapshot.exists()) {
                    fullChatHistoryLoadedRef.current = true;
                    return [];
                }
                const entries = filterVisibleChatEntries(
                    sortChatEntries(Object.entries(snapshot.val() as Record<string, IndexedChat>))
                );
                fullChatEntriesRef.current = entries;
                fullChatHistoryLoadedRef.current = true;
                setHasMoreChats(chatHistoryLimitRef.current < entries.length);
                return entries;
            })
            .finally(() => {
                fullChatHistoryPromiseRef.current = null;
            });

        return fullChatHistoryPromiseRef.current;
    }, []);

    const loadInitialChatHistory = useCallback(async (uid: string): Promise<boolean> => {
        chatHistoryLimitRef.current = CHATS_PAGE_SIZE;
        fullChatEntriesRef.current = [];
        fullChatHistoryPromiseRef.current = null;
        fullChatHistoryLoadedRef.current = false;

        const recentChatsQuery = query(
            ref(db, 'chats/'),
            orderByChild("userId"),
            equalTo(uid),
            limitToLast(CHATS_PAGE_SIZE)
        );
        const snapshot = await get(recentChatsQuery);
        if (snapshot.exists()) {
            const initialEntries = filterVisibleChatEntries(
                sortChatEntries(Object.entries(snapshot.val() as Record<string, IndexedChat>))
            );
            appendChatEntries(initialEntries, true);
            setHasMoreChats(initialEntries.length >= CHATS_PAGE_SIZE);
        } else {
            setAllChats({});
            setHasMoreChats(false);
        }

        setPendingFullHistoryUid(uid);

        return snapshot.exists();
    }, [appendChatEntries]);

    useEffect(() => {
        if (!pendingFullHistoryUid || loadingChat) return;

        let isCancelled = false;
        const idleWindow = window as typeof window & {
            requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
            cancelIdleCallback?: (handle: number) => void;
        };

        const hydrateWhenIdle = () => {
            hydrateFullChatHistory(pendingFullHistoryUid)
                .then((entries) => {
                    if (!isCancelled && entries.length > 0) showChatEntries(entries);
                })
                .catch((error) => {
                    if (!isCancelled) console.error("Error hydrating full chat history:", error);
                })
                .finally(() => {
                    if (!isCancelled) setPendingFullHistoryUid('');
                });
        };

        let idleHandle: number | null = null;
        let timeoutHandle: number | null = null;

        if (idleWindow.requestIdleCallback) {
            idleHandle = idleWindow.requestIdleCallback(hydrateWhenIdle, { timeout: 4000 });
        } else {
            timeoutHandle = window.setTimeout(hydrateWhenIdle, 500);
        }

        return () => {
            isCancelled = true;
            if (idleHandle !== null && idleWindow.cancelIdleCallback) {
                idleWindow.cancelIdleCallback(idleHandle);
            }
            if (timeoutHandle !== null) {
                window.clearTimeout(timeoutHandle);
            }
        };
    }, [hydrateFullChatHistory, loadingChat, pendingFullHistoryUid, showChatEntries]);

    // Folders can arrive after the full history: add the filed chats the loaded pages left out.
    useEffect(() => {
        foldersRef.current = folders;
        const entries = fullChatEntriesRef.current;
        if (entries.length === 0) return;
        setAllChats((prev: any) => {
            const missing = entries.filter(([chatId, chat]) => !prev[chatId] && isFiledChat(chat, folders));
            return missing.length > 0 ? { ...prev, ...Object.fromEntries(missing) } : prev;
        });
    }, [folders]);

    // Folder moves change the cached full history too, or loading more chats would put the old folders back on screen.
    const patchFullChatEntries = useCallback((chatIds: string[], patch: { folderId: string | null }) => {
        const ids = new Set(chatIds);
        fullChatEntriesRef.current = fullChatEntriesRef.current.map(([chatId, chat]) =>
            ids.has(chatId) ? [chatId, { ...chat, ...patch }] : [chatId, chat]
        );
    }, []);

    // Every chat filed in a folder, from the full history and not only the loaded pages; null when the history cannot be read.
    const chatIdsInFolder = useCallback(async (folderId: string, loadedChats: Record<string, any>): Promise<string[] | null> => {
        let entries = fullChatEntriesRef.current;
        if (!fullChatHistoryLoadedRef.current && auth.currentUser) {
            try {
                entries = await hydrateFullChatHistory(auth.currentUser.uid);
            } catch (error) {
                console.error("Error reading chat history for folder:", error);
                return null;
            }
        }
        const ids = new Set(entries.filter(([, chat]) => chat.folderId === folderId).map(([chatId]) => chatId));
        Object.keys(loadedChats).forEach((chatId) => {
            if (loadedChats[chatId]?.folderId === folderId) ids.add(chatId);
            else ids.delete(chatId);    // the loaded copy carries moves not yet in the cached history
        });
        return Array.from(ids);
    }, [hydrateFullChatHistory]);

    const loadMoreChats = useCallback(async () => {
        if (!auth.currentUser) return false;
        if (loadingMoreChats || !hasMoreChats) return false;

        const uid = auth.currentUser.uid;
        setLoadingMoreChats(true);
        chatHistoryLimitRef.current += CHATS_PAGE_SIZE;

        try {
            return showChatEntries(await hydrateFullChatHistory(uid));
        } catch (error) {
            console.error("Error loading more chats:", error);
            chatHistoryLimitRef.current -= CHATS_PAGE_SIZE;
            return false;
        } finally {
            setLoadingMoreChats(false);
        }
    }, [hasMoreChats, hydrateFullChatHistory, loadingMoreChats, showChatEntries]);

    const prefetchChatMessages = useCallback((chatId: string) => {
        if (
            chatMessagesCacheRef.current[chatId] ||
            prefetchingChatIdsRef.current.has(chatId)
        ) {
            return;
        }

        prefetchingChatIdsRef.current.add(chatId);
        get(query(
            ref(db, 'rawChatMessages/' + chatId),
            orderByKey(),
            limitToLast(INITIAL_CHAT_MESSAGE_LIMIT)
        ))
            .then((snapshot) => {
                chatMessagesCacheRef.current[chatId] = normalizeMessages(snapshot.val());
            })
            .catch((error) => {
                console.error("Error prefetching chat messages:", error);
            })
            .finally(() => {
                prefetchingChatIdsRef.current.delete(chatId);
            });
    }, []);

    useEffect(() => {
        if (searchText.length > 0) return;
        if (loadingChat) return;

        const timeoutId = window.setTimeout(() => {
            Object.keys(allChats)
                .filter((chatId) => chatId !== currentChat)
                .slice(0, 3)
                .forEach(prefetchChatMessages);
        }, 300);

        return () => window.clearTimeout(timeoutId);
    }, [allChats, currentChat, loadingChat, prefetchChatMessages, searchText]);

    // Startup work waits until a chat opened from the URL has loaded, then runs once per sign-in. It used to rerun
    // on every chat switch and every new message, re-downloading the whole chat list each time, and the next chat
    // opened waited behind it.
    const uid = auth.currentUser?.uid;
    const [startupReady, setStartupReady] = useState<boolean>(false);
    const shouldDeferStartupWork =
        !!currentChat &&
        (loadingChat || (chatMessages.length === 0 && chatMessagesCacheRef.current[currentChat] === undefined));

    useEffect(() => {
        if (!startupReady && !shouldDeferStartupWork) setStartupReady(true);
    }, [startupReady, shouldDeferStartupWork]);

    const startupUidRef = useRef<string | undefined>(undefined);

    useEffect(() => {
        if (!startupReady || !uid) return;
        let unsubscribeFunctions: Unsubscribe[] = [];
        let isMounted = true;

        // Another account signed in without leaving the page: the loaded chats are kept over new snapshots, so drop them.
        if (startupUidRef.current && startupUidRef.current !== uid) {
            setAllChats({});
            setFolders({});
            retentionPolicyAppliedForDaysRef.current = null;
        }
        startupUidRef.current = uid;

        setInitialChatHistoryLoaded(false);
        loadInitialChatHistory(uid)
            .catch((error) => {
                if (isMounted) console.error("Error loading chat history:", error);
            })
            .finally(() => {
                if (isMounted) setInitialChatHistoryLoaded(true);
            });

        const foldersQuery = query(ref(db, 'folders/'), orderByChild("userId"), equalTo(uid));
        const unsubscribeFolders = onValue(foldersQuery, (snapshot) => {
            if (snapshot.exists()) {
                setFolders(snapshot.val());
            } else {
                setFolders({});
            }
        });
        unsubscribeFunctions.push(unsubscribeFolders);

        const usersRef = ref(db, 'allowedUsers');
        const unsubscribeUsers = onValue(usersRef, (snapshot) => {
            if (snapshot.exists()) {
                setAuthorizedUsers(Object.keys(snapshot.val()));
            }
        });
        unsubscribeFunctions.push(unsubscribeUsers);

        // Cleanup function
        return () => {
            isMounted = false;
            unsubscribeFunctions.forEach(unsubscribe => unsubscribe());
        };
    }, [startupReady, uid, loadInitialChatHistory]);

    // Whether the open chat is shared publicly: its own entry only, not the shared chats of every user.
    useEffect(() => {
        setCurrentChatShared(false);
        if (!currentChat) return;
        return onValue(ref(db, 'publicChats/' + currentChat), (snapshot) => {
            setCurrentChatShared(snapshot.exists());
        });
    }, [currentChat]);


    const lastMessage = chatMessages[chatMessages.length - 1];
    const lastMessageText = lastMessage?.content?.[0]?.text || '';
    // The message the user is likely to send next (api/next_message.py), stored on the chat with the time of the
    // answer it follows: shown only while that answer is the last message.
    const storedNextMessage = allChats[currentChat]?.nextMessage;
    const nextMessageSuggestion = lastMessage?.role === 'assistant' && !lastMessage?._loading
        && storedNextMessage?.text && storedNextMessage.after === lastMessage.time
        ? String(storedNextMessage.text) : '';
    const currentChatStatus = allChats[currentChat]?.status;
    const currentChatLastActivity = allChats[currentChat]?.lastMessage;
    const isActiveChatStatus = !!currentChatStatus && !TERMINAL_CHAT_STATUSES.includes(currentChatStatus);

    // Re-check staleness while a run looks active, so a lost run recovers on its
    // own instead of waiting for the reader to reload.
    const [staleStatusTick, setStaleStatusTick] = useState<number>(0);
    useEffect(() => {
        if (!isActiveChatStatus) return;
        const intervalId = window.setInterval(
            () => setStaleStatusTick((tick) => tick + 1),
            ACTIVE_STATUS_POLL_MS
        );
        return () => window.clearInterval(intervalId);
    }, [isActiveChatStatus, currentChat]);

    const isStaleChatStatus = useMemo(() => {
        if (!isActiveChatStatus) return false;
        // This tab owns the stream, so the answer is on its way.
        if (streamingChatsRef.current.has(currentChat) || waitAnswer) return false;
        return Date.now() - (currentChatLastActivity || 0) > STALE_ACTIVE_STATUS_MS;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentChat, currentChatLastActivity, isActiveChatStatus, staleStatusTick, waitAnswer]);

    // A lost run must not leave the answer spinning or the composer disabled.
    const effectiveChatStatus = isStaleChatStatus ? 'completed' : currentChatStatus;

    useEffect(() => {
        if (isStaleChatStatus) setWaitAnswer(false);
    }, [isStaleChatStatus]);

    useEffect(() => {
        if (chatMessages && chatMessages.length > 0 && chatMessages[chatMessages.length - 1].role === 'assistant') {
            setWaitAnswer(false);
        }
    }, [chatMessages.length, lastMessageText, currentChat, currentChatStatus]);

    // Where an element sits in the chat's scrolled content.
    const contentOffset = useCallback((el: Element) => {
        const container = chatScrollContainerRef.current!;
        return el.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;
    }, []);

    // The room under an anchored question: the spacer at the end of the chat holds the part of the view the answer
    // has not filled yet, so the question can sit at the top, and it shrinks as the answer grows (the page height,
    // and so the view, stay still).
    const fitAnswerRoom = useCallback(() => {
        const container = chatScrollContainerRef.current;
        const spacer = scrollableDivRef.current;
        if (!container || !spacer) return;
        const questions = container.querySelectorAll('[data-user-question="1"]');
        const question = anchoredTurnRef.current ? questions[questions.length - 1] : undefined;
        const room = question
            ? container.clientHeight - CHAT_ANCHOR_TOP - (contentOffset(spacer) - contentOffset(question))
            : 0;
        spacer.style.height = `${Math.max(CHAT_END_SPACE, Math.round(room))}px`;
    }, [contentOffset]);

    const updateJumpToLatest = useCallback(() => {
        const container = chatScrollContainerRef.current;
        const spacer = scrollableDivRef.current;
        if (!container || !spacer) return;
        const below = contentOffset(spacer) - (container.scrollTop + container.clientHeight);
        atLatestRef.current = below <= 40;
        setShowJumpToLatest(below > 40);
        const questions = container.querySelectorAll('[data-user-question="1"]');
        const question = questions[questions.length - 1];
        questionInViewRef.current = anchoredTurnRef.current && question
            ? { text: question.textContent || '', top: contentOffset(question) - container.scrollTop }
            : null;
    }, [contentOffset]);

    const jumpToLatest = useCallback(() => {
        const container = chatScrollContainerRef.current;
        const spacer = scrollableDivRef.current;
        if (!container || !spacer) return;
        container.scrollTo({ top: Math.max(0, contentOffset(spacer) + CHAT_END_SPACE - container.clientHeight), behavior: 'smooth' });
    }, [contentOffset]);

    useEffect(() => {
        const container = chatScrollContainerRef.current;
        if (!container) return;
        if (seenChatRef.current !== currentChat) {
            // The first render after a switch still shows the previous chat's messages: wait for this chat's.
            seenChatRef.current = currentChat;
            openedChatRef.current = '';
            anchoredTurnRef.current = false;
            atLatestRef.current = true;
            setShowJumpToLatest(false);
            return;
        }
        const frame = window.requestAnimationFrame(() => {
            const questions = container.querySelectorAll('[data-user-question="1"]');
            const question = questions[questions.length - 1];
            if (openedChatRef.current !== currentChat) {
                if (loadingChat || chatMessages.length === 0) return;
                // A chat opens at its latest message, or at its question when the answer is still being written.
                openedChatRef.current = currentChat;
                anchoredTurnRef.current = isActiveChatStatus && !!question;
                fitAnswerRoom();
                container.scrollTop = anchoredTurnRef.current && question
                    ? Math.max(0, contentOffset(question) - CHAT_ANCHOR_TOP)
                    : container.scrollHeight;
            } else if (anchorNextQuestionRef.current && question) {
                anchorNextQuestionRef.current = false;
                anchoredTurnRef.current = true;
                fitAnswerRoom();
                anchorScrollUntilRef.current = Date.now() + 700;
                container.scrollTo({ top: Math.max(0, contentOffset(question) - CHAT_ANCHOR_TOP), behavior: 'smooth' });
            } else {
                fitAnswerRoom();
                // Messages replaced or loaded above the anchored question (the final messages when a stream ends,
                // earlier history) must not move it: put it back where the reader left it.
                const kept = questionInViewRef.current;
                if (anchoredTurnRef.current && question && kept && kept.text === (question.textContent || '')
                    && Date.now() > anchorScrollUntilRef.current) {
                    const drift = contentOffset(question) - container.scrollTop - kept.top;
                    if (Math.abs(drift) > 1) container.scrollTop += drift;
                }
                // Nothing is being written toward the reader: content that arrives while they are at the end (earlier
                // history, a message from another tab) keeps them there.
                if (!anchoredTurnRef.current && atLatestRef.current && !isActiveChatStatus) {
                    container.scrollTop = container.scrollHeight;
                }
            }
            updateJumpToLatest();
        });
        return () => window.cancelAnimationFrame(frame);
    }, [chatMessages, currentChat, loadingChat, isActiveChatStatus, contentOffset, fitAnswerRoom, updateJumpToLatest]);

    // The page only opens after sign-in (App.tsx), but the Firebase session can end while it is open: a refresh
    // token that stops working, or browser storage that is lost (CHATSOC-2V). Say so instead of failing silently;
    // the reader's draft stays in the box.
    const notifySessionLost = useCallback(() => {
        Sentry.captureMessage('Firebase session ended while the chat was open', 'warning');
        if (toast.isActive('session-lost')) return;
        toast({
            id: 'session-lost',
            position: 'top',
            title: 'Your session has ended',
            description: 'Reload the page to sign in again. Your message is still in the box.',
            status: 'warning',
            duration: null,
            isClosable: true,
        });
    }, [toast]);

    const startNewDialogue = async () => {
        if (allChats && allChats[Object.keys(allChats)[0]] && !allChats[Object.keys(allChats)[0]].status) {
            setCurrentChat(Object.keys(allChats)[0]);
        } else {
            const uid = auth.currentUser?.uid;
            if (!uid) {
                notifySessionLost();
                return;
            }
            // Create chat locally in Firebase - instant, no API call needed
            const chatId = createChatLocal(uid, responseMode);
            const now = Date.now();

            // Optimistically add to allChats immediately for instant UI feedback
            setAllChats((prev: any) => ({
                [chatId]: {
                    title: "New Chat",
                    time: now,
                    lastMessage: now,
                    userId: uid,
                    incomingUrl: window.location.hostname.split('.com')[0],
                    environment: getEnvironment(),
                    provider: providerForMode(responseMode)
                },
                ...prev
            }));

            setCurrentChat(chatId);
        }
    };

    const sendMessage = useCallback(async (overrideText?: string) => {
        // Guard against non-string values (e.g. the MouseEvent from onClick={sendMessage})
        const messageText = typeof overrideText === 'string' ? overrideText : dialogueBox;
        if (sendingChatsRef.current.has(currentChat)) return;
        sendingChatsRef.current.add(currentChat);

        try {
            if (messageText.length === 0) {
                return;
            }
            if (!auth.currentUser) {
                notifySessionLost();
                return;
            }

            suppressLegalDraftAutoOpenForChatRef.current = null;

            const now = Date.now();
            const lastMinute = sentTime.filter((x) => x > now - 60000);
            // If more than 5 messages have been sent in the last minute, don't send the message
            if (lastMinute.length > 5) {
                toast({
                    position: 'top',
                    title: 'Too many messages sent',
                    description: "Please wait a minute before sending more messages.",
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                })
                return;
            }
            // Keep only timestamps from the last minute + the new one
            setSentTime([...lastMinute, now]);
            setWaitAnswer(true);
            if (!currentChat) {
                await startNewDialogue();
                setNewDialogueReady(true);
                setWaitAnswer(false);
            } else {
                const uploadedFiles = dialogueFiles
                    .filter(df => df.id)
                    .map(df => ({ id: df.id!, title: df.name }));
                if (messageText.length < 14000) {
                    const text = `${messageText}`;
                    setDialogueBox('');

                    // Optimistic update: immediately show user message and loading assistant response
                    const optimisticUserMessage = {
                        role: 'user',
                        content: [{ type: 'text', text: text }],
                        time: Date.now(),
                        uploadedFiles: uploadedFiles.length > 0 ? uploadedFiles : undefined,
                        _optimistic: true
                    };
                    const optimisticAssistantMessage = {
                        role: 'assistant',
                        content: [{ type: 'text', text: '' }],
                        _loading: true
                    };
                    anchorNextQuestionRef.current = true;
                    setChatMessages((prev: any) => [...prev, optimisticUserMessage, optimisticAssistantMessage]);
                    setDialogueFiles([]);

                    let receivedStreamText = false;

                    // The stream went quiet or closed before the run reported
                    // its end. The server keeps the turn, so keep what arrived
                    // here too, and let the chat's own status decide when the
                    // spinner stops: a live run keeps reporting, a lost one
                    // goes stale within a minute.
                    const releaseStreamedTurn = () => {
                        streamingChatsRef.current.delete(currentChat);
                        setChatMessages((prev: any) => prev
                            .filter((message: any) =>
                                !(message._loading && !(message.content?.[0]?.text || '').trim()))
                            .map((message: any) => {
                                const { _loading, _optimistic, _status, ...rest } = message;
                                return rest;
                            }));
                        setWaitAnswer(false);
                    };

                    try {
                        streamingChatsRef.current.add(currentChat);

                        const markStreamStatus = (status: string) => {
                            setAllChats((prev: any) => ({
                                ...prev,
                                [currentChat]: {
                                    ...prev[currentChat],
                                    status,
                                    lastMessage: Date.now()
                                }
                            }));
                            setChatMessages((prev: any) => prev.map((message: any, index: number) =>
                                index === prev.length - 1 && message._loading
                                    ? { ...message, _status: status }
                                    : message
                            ));
                        };

                        const appendStreamDelta = (delta: string) => {
                            receivedStreamText = true;
                            setChatMessages((prev: any) => {
                                const next = [...prev];
                                const lastIndex = next.length - 1;
                                const lastMessage = next[lastIndex];

                                if (lastMessage?.role === 'assistant' && lastMessage._loading) {
                                    const currentText = lastMessage.content?.[0]?.text || '';
                                    next[lastIndex] = {
                                        ...lastMessage,
                                        content: [{
                                            ...lastMessage.content[0],
                                            text: currentText + delta
                                        }],
                                        _status: 'writing'
                                    };
                                    return next;
                                }

                                return [
                                    ...next,
                                    {
                                        role: 'assistant',
                                        content: [{ type: 'text', text: delta }],
                                        duration: 0,
                                        _loading: true,
                                        _status: 'writing'
                                    }
                                ];
                            });
                        };

                        const response = await fetchSendMessage(
                            text,
                            currentChat,
                            apiAddress,
                            responseMode,
                            uploadedFiles.length > 0 ? uploadedFiles : undefined,
                            useUserProfile,
                            {
                                onStatus: markStreamStatus,
                                onDelta: appendStreamDelta,
                                onHeartbeat: () => {
                                    setAllChats((prev: any) => ({
                                        ...prev,
                                        [currentChat]: {
                                            ...prev[currentChat],
                                            lastMessage: Date.now()
                                        }
                                    }));
                                },
                                onDone: (messages: any[]) => {
                                    streamingChatsRef.current.delete(currentChat);
                                    setAllChats((prev: any) => ({
                                        ...prev,
                                        [currentChat]: {
                                            ...prev[currentChat],
                                            status: 'completed',
                                            lastMessage: Date.now()
                                        }
                                    }));
                                    if (messages.length > 0) {
                                        const fullMessages = normalizeMessages(messages);
                                        chatMessagesCacheRef.current[currentChat] = fullMessages;
                                        setChatMessages(fullMessages);
                                    }
                                    setWaitAnswer(false);
                                }
                            }
                        );
                        if (response._status === 429) {
                            // Rate limit hit — remove optimistic messages
                            setChatMessages((prev: any) => prev.filter((m: any) => !m._optimistic && !m._loading));
                            setWaitAnswer(false);
                            setDialogueBox(text);

                            if (response.limit === 'dialogue') {
                                toast({
                                    position: 'top',
                                    title: "You've Reached the Limit for This Chat",
                                    description: "This conversation is at the system's maximum limit. To keep things running smoothly, please start a new chat to continue. Thanks for your understanding!",
                                    status: 'warning',
                                    duration: 8000,
                                    isClosable: true,
                                });
                            } else {
                                toast({
                                    position: 'top',
                                    title: 'Slow down!',
                                    description: "You've sent too many messages recently. Please wait a few minutes before trying again.",
                                    status: 'warning',
                                    duration: 8000,
                                    isClosable: true,
                                });
                            }
                            return;
                        } else if (response._streamLost) {
                            releaseStreamedTurn();
                            return;
                        } else if (response.lastMessage) {
                            //Error handling - remove optimistic messages
                            setChatMessages((prev: any) => prev.filter((m: any) => !m._optimistic && !m._loading));
                            toast({
                                position: 'top',
                                title: 'Error',
                                description: "There was a problem processing your request. Please try again in a few seconds. If the issue persists, try again in a few minutes.",
                                status: 'warning',
                                duration: 5000,
                                isClosable: true,
                            })
                            setWaitAnswer(false);
                            return setDialogueBox(response.lastMessage);
                        } else if (response.error) {
                            setChatMessages((prev: any) => prev.filter((m: any) => !m._optimistic && !m._loading));
                            toast({
                                position: 'top',
                                title: 'Error',
                                description: response.error || "An error occurred",
                                status: 'error',
                                duration: 5000,
                                isClosable: true,
                            })
                            setWaitAnswer(false);
                            return setDialogueBox(text);
                        }
                        setWaitAnswer(false);
                    } catch (error) {
                        if (error instanceof StreamInactivityError || receivedStreamText) {
                            releaseStreamedTurn();
                            return;
                        }
                        streamingChatsRef.current.delete(currentChat);
                        // Remove optimistic messages on error
                        setChatMessages((prev: any) => prev.filter((m: any) => !m._optimistic && !m._loading));
                        setWaitAnswer(false);
                        setDialogueBox(text);
                    } finally {
                        streamingChatsRef.current.delete(currentChat);
                    }
                    const lastMessage = Date.now();
                    update(ref(db, 'chats/' + currentChat), {
                        lastMessage
                    });
                } else {
                    toast({
                        position: 'top',
                        title: 'The message is too long',
                        description: "Please shorten it to less than 14k characters.",
                        status: 'error',
                        duration: 5000,
                        isClosable: true,
                    });
                    setWaitAnswer(false);
                }
            }
        } finally {
            sendingChatsRef.current.delete(currentChat);
        }
    }, [dialogueBox, currentChat, dialogueFiles, apiAddress, responseMode, sentTime, startNewDialogue, toast, useUserProfile, notifySessionLost]);

    // Shared soft-delete: extract analytics, mark as deleted, purge messages
    const softDeleteChats = useCallback(async (chatIds: string[]) => {
        if (!chatIds.length) return;

        // Fetch all messages in parallel for analytics extraction
        const messagesSnapshots = await Promise.all(
            chatIds.map(chatId => get(ref(db, '/rawChatMessages/' + chatId)))
        );

        // Build a single multi-path update
        const batchUpdate: Record<string, any> = {};
        const now = Date.now();

        chatIds.forEach((chatId, i) => {
            const msgSnapshot = messagesSnapshots[i];
            let analyticsData: Record<string, any> = {};

            if (msgSnapshot.exists()) {
                const messages = msgSnapshot.val() as any[];
                const turns = messages.filter((m: any) =>
                    m.role === 'user' &&
                    !(Array.isArray(m.content) && m.content.some((c: any) => c.type === 'tool_result'))
                ).length;
                let thumbsUp = 0;
                let thumbsDown = 0;
                messages.forEach((m: any) => {
                    if (m.review?.thumbs === 'up') thumbsUp++;
                    if (m.review?.thumbs === 'down') thumbsDown++;
                });
                analyticsData = {
                    numberOfTurns: turns,
                    thumbsUpCount: thumbsUp,
                    thumbsDownCount: thumbsDown
                };
            }

            // Soft-delete chat metadata with analytics
            batchUpdate[`/chats/${chatId}/status`] = 'deleted';
            batchUpdate[`/chats/${chatId}/deletedAt`] = now;
            Object.entries(analyticsData).forEach(([key, value]) => {
                batchUpdate[`/chats/${chatId}/${key}`] = value;
            });

            // Hard-delete messages
            batchUpdate[`/rawChatMessages/${chatId}`] = null;
            batchUpdate[`/alternativeMessages/${chatId}`] = null;
            batchUpdate[`/legalDraftSessions/${chatId}`] = null;
        });

        // Single atomic write
        await update(ref(db), batchUpdate);

        // Remove from local state
        setAllChats((prev: any) => {
            const updated = { ...prev };
            chatIds.forEach(id => delete updated[id]);
            return updated;
        });

        const deletedChatIds = new Set(chatIds);
        fullChatEntriesRef.current = fullChatEntriesRef.current.filter(([id]) => !deletedChatIds.has(id));
        chatIds.forEach(id => {
            delete chatMessagesCacheRef.current[id];
            prefetchingChatIdsRef.current.delete(id);
        });
    }, []);

    useEffect(() => {
        if (!auth.currentUser || companyChatRetentionDays === null) return;
        if (
            !initialChatHistoryLoaded ||
            loadingChat ||
            retentionPolicyAppliedForDaysRef.current === companyChatRetentionDays
        ) return;

        // Once per session (and per retention setting). Opening a chat no longer cancels it, and it reads the same
        // full history as the sidebar instead of downloading the chat list again.
        retentionPolicyAppliedForDaysRef.current = companyChatRetentionDays;
        const uid = auth.currentUser.uid;
        const applyRetentionPolicy = async () => {
            try {
                const entries = await hydrateFullChatHistory(uid);
                const expiredChatIds = getExpiredChatIds(entries, companyChatRetentionDays);
                if (expiredChatIds.length === 0) return;

                await softDeleteChats(expiredChatIds);

                if (expiredChatIds.includes(currentChatIdRef.current)) {
                    setCurrentChat('');
                    setChatMessages([]);
                    navigate('/chat');
                }
            } catch (error) {
                console.error('Error applying chat retention policy:', error);
                retentionPolicyAppliedForDaysRef.current = null;   // try again later in the session
            }
        };

        applyRetentionPolicy();
    }, [companyChatRetentionDays, hydrateFullChatHistory, initialChatHistoryLoaded, loadingChat, navigate, softDeleteChats]);

    function deleteDialogue() {
        softDeleteChats([currentChat]).then(() => {
            setCurrentChat('');
            navigate('/');
        });
    }

    const rewindToMessage = useCallback(async (messageIndex: number, questionText: string) => {
        if (!currentChat || waitAnswer) return;

        // Truncate messages in Firebase to everything before this user message
        const truncated = chatMessages.slice(0, messageIndex);
        await set(ref(db, 'rawChatMessages/' + currentChat), truncated.length > 0 ? truncated : null);

        // Clear alternative messages since the conversation changed
        await remove(ref(db, 'alternativeMessages/' + currentChat));

        // Clear references since they may no longer be valid
        await update(ref(db, 'chats/' + currentChat), { references: null });

        // Pre-fill the input with the old question
        setDialogueBox(questionText);

        toast({
            position: 'top',
            title: 'Conversation rewound',
            description: 'Edit your question and send again.',
            status: 'info',
            duration: 3000,
            isClosable: true,
        });
    }, [currentChat, chatMessages, waitAnswer, toast]);

    const bulkDeleteChats = useCallback(async (chatIds: string[]) => {
        if (!chatIds.length) return;

        await softDeleteChats(chatIds);

        // If current chat was deleted, navigate away
        if (chatIds.includes(currentChat)) {
            setCurrentChat('');
            navigate('/');
        }

        toast({
            position: 'top',
            title: `Deleted ${chatIds.length} chat(s)`,
            status: 'success',
            duration: 3000,
            isClosable: true,
        });
    }, [currentChat, navigate, toast, softDeleteChats]);

    const deleteAllChats = useCallback(async () => {
        if (!auth.currentUser) return;

        try {
            // Fetch ALL user chats from Firebase (not just paginated in-memory state)
            const allChatsQuery = query(ref(db, 'chats/'), orderByChild("userId"), equalTo(auth.currentUser.uid));
            const snapshot = await get(allChatsQuery);

            if (!snapshot.exists()) return;

            const allUserChats = snapshot.val() as Record<string, ChatInterface>;
            const chatIds = Object.keys(allUserChats).filter(id => allUserChats[id].status !== 'deleted');

            if (chatIds.length === 0) return;

            // Soft-delete all chats (analytics extraction + batch write)
            await softDeleteChats(chatIds);

            // Delete all user folders
            const folderUpdates: Record<string, any> = {};
            Object.keys(folders).forEach(folderId => {
                folderUpdates[`/folders/${folderId}`] = null;
            });
            if (Object.keys(folderUpdates).length > 0) {
                await update(ref(db), folderUpdates);
            }

            // Reset UI state
            setFolders({});
            setCurrentChat('');
            setChatMessages([]);
            navigate('/');

            toast({
                position: 'top',
                title: 'All chats deleted',
                description: 'Your conversation history has been cleared.',
                status: 'success',
                duration: 4000,
                isClosable: true,
            });
        } catch (error) {
            console.error('Error deleting all chats:', error);
            toast({
                position: 'top',
                title: 'Failed to delete chats',
                description: 'An error occurred while deleting your chats. Please try again.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
            throw error; // Re-throw so caller knows it failed
        }
    }, [folders, navigate, toast, softDeleteChats]);

    function updatePublicChats() {
        if (currentChatShared) {
            remove(ref(db, '/publicChats/' + currentChat))
        } else {
            update(ref(db, 'publicChats'), {
                [currentChat]: true
            })
        }
    }


    function publicChatToClipboard(url: string) {
        navigator.clipboard.writeText(url)
        toast({
            position: 'top',
            title: 'The URL was copied to your clipboard',
            status: 'success',
            duration: 5000,
            isClosable: true,
        })

    }

    const handleFileRemove = useCallback((index: number) => {
        setDialogueFiles((prev) => prev.filter((_, i) => i !== index));
    }, []);

    function handleTitleChange(title: string) {
        if (title !== allChats[currentChat].title) {
            update(ref(db, 'chats/' + currentChat), {
                title: title
            })
            toast({
                position: 'top',
                title: 'Title updated',
                status: 'success',
                duration: 5000,
                isClosable: true,
            })
        }
    }

    const createFolder = useCallback(async (): Promise<string> => {
        const folderId = `folder_${Date.now()}`;
        const now = Date.now();
        const newFolder: FolderInterface = {
            id: folderId,
            title: "New Folder",
            userId: auth.currentUser?.uid || '',
            createdAt: now,
            lastChatMovedAt: now
        };

        await update(ref(db, 'folders/' + folderId), newFolder);
        return folderId;
    }, []);

    const moveChatToFolder = useCallback(async (chatId: string, folderId: string) => {
        const movedAt = Date.now();
        // Optimistic local update
        setAllChats((prev: any) => ({
            ...prev,
            [chatId]: { ...prev[chatId], folderId }
        }));
        patchFullChatEntries([chatId], { folderId });
        setFolders((prev) => ({
            ...prev,
            [folderId]: {
                ...prev[folderId],
                lastChatMovedAt: movedAt
            }
        }));
        await Promise.all([
            update(ref(db, 'chats/' + chatId), { folderId: folderId }),
            update(ref(db, 'folders/' + folderId), { lastChatMovedAt: movedAt })
        ]);
    }, [patchFullChatEntries]);

    const removeChatFromFolder = useCallback(async (chatId: string) => {
        const folderId = allChats[chatId]?.folderId;

        // Optimistic local update
        setAllChats((prev: any) => {
            const updated = { ...prev, [chatId]: { ...prev[chatId], folderId: null } };
            return updated;
        });
        patchFullChatEntries([chatId], { folderId: null });
        await update(ref(db, 'chats/' + chatId), { folderId: null });

        // Delete the folder only if no chat in the whole history is left in it (not just none of the loaded ones)
        if (folderId) {
            const remainingChats = await chatIdsInFolder(folderId, { ...allChats, [chatId]: { ...allChats[chatId], folderId: null } });
            if (remainingChats !== null && remainingChats.length === 0) {
                await remove(ref(db, 'folders/' + folderId));
            }
        }
    }, [allChats, chatIdsInFolder, patchFullChatEntries]);

    const updateFolderTitle = useCallback(async (folderId: string, title: string) => {
        await update(ref(db, 'folders/' + folderId), { title: title });
    }, []);

    const deleteFolder = useCallback(async (folderId: string) => {
        // Remove folderId from every chat in the folder, loaded or not
        const chatsInFolder = (await chatIdsInFolder(folderId, allChats))
            ?? Object.keys(allChats).filter(chatId => allChats[chatId].folderId === folderId);
        patchFullChatEntries(chatsInFolder, { folderId: null });
        setAllChats((prev: any) => {
            const updated = { ...prev };
            chatsInFolder.forEach(chatId => {
                if (updated[chatId]) updated[chatId] = { ...updated[chatId], folderId: null };
            });
            return updated;
        });

        if (chatsInFolder.length > 0) {
            await Promise.all(chatsInFolder.map(chatId => update(ref(db, 'chats/' + chatId), { folderId: null })));
        }
        await remove(ref(db, 'folders/' + folderId));
    }, [allChats, chatIdsInFolder, patchFullChatEntries]);

    const appPaneWidth = showBar ? "80%" : "100%";
    const chatPaneWidth = activeLegalDraft && isDesktop ? "50%" : "100%";
    const fixedTopBarWidth = activeLegalDraft && isDesktop
        ? (showBar ? "40%" : "50%")
        : appPaneWidth;
    const chatContentMaxW = activeLegalDraft && isDesktop ? "min(760px, calc(100% - 40px))" : "800px";
    const legalDraftChatContext = useMemo(
        () => chatMessages.map(getMessageText).filter(Boolean).join('\n').slice(-12000),
        [chatMessages],
    );

    return (
        <Flex
            position="fixed"
            inset={0}
            h="100dvh"
            maxH="100dvh"
            w="100%"
            overflow="hidden"
        >
            {showBar &&
                <Sidebar
                    isDesktop={isDesktop}
                    showBar={showBar}
                    setShowBar={setShowBar}
                    allChats={allChats}
                    folders={folders}
                    createFolder={createFolder}
                    moveChatToFolder={moveChatToFolder}
                    removeChatFromFolder={removeChatFromFolder}
                    updateFolderTitle={updateFolderTitle}
                    deleteFolder={deleteFolder}
                    currentChat={currentChat}
                    setCurrentChat={setCurrentChat}
                    startNewDialogue={startNewDialogue}
                    searchText={searchText}
                    setSearchText={setSearchText}
                    matchedChats={matchedChats}
                    loadingSearch={loadingSearch}
                    setApiAddress={setApiAddress}
                    apiAddress={apiAddress}
                    navigate={navigate}
                    loadMoreChats={loadMoreChats}
                    hasMoreChats={hasMoreChats}
                    loadingMoreChats={loadingMoreChats}
                    onBulkDelete={bulkDeleteChats}
                    onDeleteAllChats={deleteAllChats}
                />}
            <Flex zIndex={1} w={appPaneWidth} minW={0} h="100%" minH={0} overflow="hidden">
                <Flex {...getRootProps()} zIndex={1} w={chatPaneWidth} minW={0} h="100%" minH={0} overflow="hidden" direction="column" display={activeLegalDraft && !isDesktop ? "none" : "flex"}>
                    {DOCUMENT_UPLOAD_ENABLED && <input {...getInputProps()} />}
                    {DOCUMENT_UPLOAD_ENABLED && isDragActive ? <Text top="50%" left="50%" fontSize="40px" fontWeight={300} color="gray" position="fixed">Drop your file here</Text> : null}
                    <Box
                        style={{ WebkitMaskImage: isDragActive ? "linear-gradient(to bottom, rgba(0,0,0,.4) 0%, rgba(0,0,0,0))" : "" }}
                        zIndex={2}
                        position='fixed'
                        h="48px"
                        bg={!currentChat ? "transparent" : topBarBg}
                        borderBottom={currentChat ? "1px solid" : ""}
                        borderColor={borderColor}
                        boxShadow={currentChat ? "0 1px 3px 0 rgba(0, 0, 0, 0.05)" : "none"}
                        w={fixedTopBarWidth}
                        transition="background-color 0.2s, border-color 0.2s, box-shadow 0.2s"
                    >
                        <Center h="48px" px={4}>
                            {!showBar && isDesktop && (
                                <Tooltip label="Open sidebar" placement="bottom">
                                    <IconButton
                                        onClick={() => { setShowBar((prev) => !prev); }}
                                        variant="ghost"
                                        aria-label="Open sidebar"
                                        icon={<BsLayoutSidebarInset />}
                                        borderRadius="full"
                                        color={iconColor}
                                        _hover={{
                                            bg: iconHover
                                        }}
                                    />
                                </Tooltip>
                            )}
                            <Spacer />
                            <ColorModeSwitcher mr={2} />
                            <Flex w="100%" justify="center" align="center">
                                {allChats[currentChat] &&
                                    <Tooltip label="Click to edit title" placement="bottom">
                                        <Editable
                                            key={currentChat}
                                            defaultValue={allChats[currentChat].title}
                                            fontSize="17px"
                                            fontWeight={600}
                                            fontFamily="Elza, sans-serif"
                                            isPreviewFocusable={true}
                                            submitOnBlur={true}
                                            onSubmit={(newTitle) => { handleTitleChange(newTitle) }}
                                        >
                                            <EditablePreview
                                                noOfLines={1}
                                                px={3}
                                                py={1.5}
                                                borderRadius="md"
                                                _hover={{
                                                    bg: startButtonHover
                                                }}
                                            />
                                            <EditableInput textAlign="center" w="60vw" />
                                        </Editable>
                                    </Tooltip>
                                }

                            </Flex>
                            <Spacer />
                            {currentChat && <Menu size="md" placement="bottom-end">
                                <MenuButton
                                    zIndex={3}
                                    as={IconButton}
                                    variant="ghost"
                                    icon={<FaEllipsisV />}
                                    borderRadius="full"
                                    color={iconColor}
                                    _hover={{
                                        bg: iconHover
                                    }}
                                />
                                <MenuList borderRadius="xl" boxShadow="lg" border="1px solid" borderColor={borderColor} bg={menuBg} py={2} minW="240px">
                                    <Box px={3} py={2} mb={1}>
                                        <Flex align="center" justify="space-between" gap={2}>
                                            <Flex align="center" gap={2}>
                                                <Box color={iconColor}>
                                                    <FaShareAlt size={14} />
                                                </Box>
                                                <Text fontWeight={600} color={menuText} fontSize="14px">
                                                    Share publicly
                                                </Text>
                                            </Flex>
                                            <Switch
                                                id="ChatSOC - Enable Shared Publicly"
                                                isChecked={currentChatShared}
                                                onChange={(e) => { updatePublicChats() }}
                                                colorScheme="blue"
                                                size="sm"
                                            />
                                        </Flex>
                                    </Box>
                                    {currentChatShared && (
                                        <Box px={4} pb={2}>
                                            <InputGroup size="sm">
                                                <Input
                                                    isDisabled={true}
                                                    value={`${process.env.REACT_APP_CURRENT_ENV === 'staging' ? 'https://staging-chatsocwebapp.sullivanoncomp.com' : 'https://chatsocwebapp.sullivanoncomp.com'}/chatHistory?chatId=${currentChat}`}
                                                    bg={inputBg}
                                                    borderRadius="md"
                                                    fontSize="12px"
                                                    pr="40px"
                                                />
                                                <InputRightElement>
                                                    <Tooltip label="Copy to clipboard" placement="top">
                                                        <IconButton
                                                            id="ChatSOC - Copy Public Share Link"
                                                            variant="ghost"
                                                            aria-label="Clipboard"
                                                            icon={<FaClipboard />}
                                                            size="xs"
                                                            color={iconColor}
                                                            _hover={{ bg: iconHover }}
                                                            onClick={() => { publicChatToClipboard(`${process.env.REACT_APP_CURRENT_ENV === 'staging' ? 'https://staging-chatsocwebapp.sullivanoncomp.com' : 'https://chatsocwebapp.sullivanoncomp.com'}/chatHistory?chatId=${currentChat}`) }}
                                                        />
                                                    </Tooltip>
                                                </InputRightElement>
                                            </InputGroup>
                                        </Box>
                                    )}
                                    <MenuDivider my={2} borderColor={borderColor} />
                                    <MenuItem
                                        fontWeight={500}
                                        color={menuText}
                                        onClick={() => {
                                            downloadPdf({
                                                title: allChats[currentChat]?.title,
                                                messages: chatMessages,
                                                allReferences: memoizedReferences,
                                                time: allChats[currentChat]?.time,
                                            });
                                        }}
                                        isDisabled={isGeneratingPdf || !chatMessages?.length}
                                        icon={<FaFilePdf size={16} />}
                                        borderRadius="md"
                                        mx={2}
                                        fontSize="14px"
                                        bg="transparent"
                                        _hover={{ bg: menuHover }}
                                    >
                                        Save as PDF
                                    </MenuItem>
                                    <MenuItem
                                        fontWeight={500}
                                        color="red.600"
                                        onClick={() => { deleteDialogue(); }}
                                        icon={<FaTrash size={16} />}
                                        borderRadius="md"
                                        mx={2}
                                        fontSize="14px"
                                        bg="transparent"
                                        _hover={{ bg: deleteHover }}
                                    >
                                        Delete
                                    </MenuItem>
                                </MenuList>
                            </Menu>}
                            {currentChat && (
                                <Tooltip label="Close chat" placement="bottom">
                                    <CloseButton
                                        onClick={() => { setCurrentChat(''); setDialogueBox("") }}
                                        borderRadius="full"
                                        color={iconColor}
                                        _hover={{
                                            bg: iconHover
                                        }}
                                    />
                                </Tooltip>
                            )}
                            {!isDesktop && (
                                <IconButton
                                    onClick={() => { setShowBar(!showBar); }}
                                    aria-label="Bars"
                                    variant="ghost"
                                    color={iconColor}
                                    icon={<FaBars />}
                                    borderRadius="full"
                                    _hover={{
                                        bg: iconHover
                                    }}
                                />
                            )}
                        </Center>
                    </Box>
                    <Box ref={chatScrollContainerRef} onScroll={updateJumpToLatest} style={{ WebkitMaskImage: isDragActive ? "linear-gradient(to bottom, rgba(0,0,0,.4) 0%, rgba(0,0,0,0))" : "linear-gradient(to bottom, rgba(0,0,0,1) 95%, rgba(0,0,0,0))" }} fontFamily={'"Elza", sans-serif'} flex={1} minH={0} overflowY="auto" bg={mainBg} pt="48px">
                        <Box maxW={chatContentMaxW} mx="auto" px={{ base: 4, md: activeLegalDraft && isDesktop ? 4 : 6 }}>
                            {!currentChat ?
                                <Box marginTop="4%">
                                    <Center marginBottom="4%" display="block" >
                                        <Box bg='transparent' borderRadius="full" p={2} w="fit-content" mx="auto" mb="20px">
                                            <Image src="./chatsoc_logo.svg" w="260px" />
                                        </Box>
                                        <Text color={textColor} textAlign="center" paddingLeft="2%" paddingRight="2%" fontWeight={600}>Submit your question in the chat box below to get an AI response informed by the content in Sullivan on Comp</Text>
                                    </Center>
                                    {isDesktop && <Box>
                                        <Center marginTop="-4%">
                                            <Box w="100%">
                                                <Center marginTop="4%" padding="2%">
                                                    <Flex direction={{ base: "column", md: "row" }} gap="4%" width="100%" justify="space-between" wrap="wrap">
                                                        {startButtons.map((x, idx) => {
                                                            return (
                                                                <Button
                                                                    id="ChatSOC - New Chat Created"
                                                                    onClick={() => { startNewDialogue().then(() => { setDialogueBox(x.question) }); }}
                                                                    marginBottom="2%"
                                                                    boxShadow="base"
                                                                    w="48%"
                                                                    textAlign="left"
                                                                    variant="outline"
                                                                    padding="20px"
                                                                    paddingLeft="2%"
                                                                    paddingRight="2%"
                                                                    height="auto"
                                                                    overflow="hidden"
                                                                    key={idx}
                                                                    _hover={{ bg: startButtonHover }}
                                                                    borderColor={borderColor}
                                                                >
                                                                    <Box w="100%">
                                                                        <Text color={textColor} fontSize="14px" fontWeight={600} overflow="hidden" whiteSpace="normal">
                                                                            {x.title}
                                                                        </Text>
                                                                        <Text marginTop="1%" fontWeight={300} fontSize="12px" color={secondaryTextColor} overflow="hidden" whiteSpace="normal">
                                                                            {x.question}
                                                                        </Text>
                                                                    </Box>
                                                                </Button>
                                                            )
                                                        }
                                                        )}
                                                    </Flex>
                                                </Center>

                                            </Box>
                                        </Center>
                                    </Box>}
                                </Box>
                                :
                                loadingChat ? (
                                    <Flex h="100%" w="100%" direction="column" align="center" justify="center" mt="100px" opacity={0.8}>
                                        <Spinner
                                            size="xl"
                                            color={textColor}
                                            thickness="3px"
                                            speed="0.8s"
                                            emptyColor={borderColor}
                                        />
                                        <Text mt={5} color={secondaryTextColor} fontSize="sm" fontWeight="500" letterSpacing="wide">
                                            Loading history...
                                        </Text>
                                    </Flex>
                                ) : (
                                    <ChatMessages
                                        status={effectiveChatStatus}
                                        chatMessages={chatMessages}
                                        currentChat={currentChat}
                                        allReferences={memoizedReferences}
                                        apiAddress={apiAddress}
                                        onRewind={isPartialChatHistory ? undefined : rewindToMessage}
                                        waitAnswer={waitAnswer}
                                        canLinkCaseCitations={canLinkCaseCitations}
                                        onOpenLegalDraft={openLegalDraft}
                                        sourceVisibilityByMessage={sourceVisibilityByMessage}
                                        onSourceVisibilityChange={handleSourceVisibilityChange}
                                    />
                                )
                            }
                            <div style={{ height: `${CHAT_END_SPACE}px` }} ref={scrollableDivRef} />
                        </Box>
                    </Box>
                    <Box flexShrink={0} px={{ base: 4, md: activeLegalDraft && isDesktop ? 4 : 6 }} pt={3} pb={{ base: 3, md: 4 }} bg={mainBg} position="relative">
                        {showJumpToLatest && currentChat && (
                            <Button
                                position="absolute" top="-46px" left="50%" transform="translateX(-50%)" zIndex={3}
                                size="sm" borderRadius="full" px={4} fontWeight={600} fontSize="13px"
                                leftIcon={<FaArrowDown size={11} />}
                                bg={jumpButtonBg} color={jumpButtonColor} border="1px solid" borderColor={borderColor}
                                boxShadow="0 4px 14px rgba(0, 0, 0, 0.12)" _hover={{ bg: jumpButtonHoverBg }}
                                onClick={jumpToLatest} aria-label="Scroll to the latest message"
                            >
                                Latest
                            </Button>
                        )}
                        <Box maxW={chatContentMaxW} mx="auto" w="100%">
                            <DialogueInput
                                useAutocomplete={useAutocomplete}
                                nextMessageSuggestion={useAutocomplete && !waitAnswer ? nextMessageSuggestion : ''}
                                dialogueBox={dialogueBox}
                                handleDialogueBoxChange={handleDialogueBoxChange}
                                sendMessage={sendMessage}
                                chatMessages={chatMessages}
                                serverContextTokens={isPartialChatHistory ? allChats[currentChat]?.contextTokens : undefined}
                                waitAnswer={waitAnswer}
                                isDesktop={isDesktop}
                                apiAddress={apiAddress}
                                chatStatus={effectiveChatStatus}
                                currentChat={currentChat}
                                responseMode={responseMode}
                                handleResponseModeChange={handleResponseModeChange}
                                dialogueFiles={dialogueFiles}
                                onFileSelect={startFileUpload}
                                onFileRemove={handleFileRemove}
                                acceptedFormats={acceptedFormats}
                                enableFileUpload={DOCUMENT_UPLOAD_ENABLED}
                            />
                            <Text mt={3} fontSize="12px" lineHeight="1.45" color={secondaryTextColor} textAlign="center">
                                ChatSOC can make mistakes. Always verify important information before relying on it. Do not share confidential information; it's not necessary for obtaining useful responses.
                            </Text>
                        </Box>
                    </Box>
                </Flex>
                {activeLegalDraft && (
                    <Box w={isDesktop ? "50%" : "100%"} minW={0} h="100%" minH={0} overflow="hidden">
                        <LegalDraftPanel
                            artifact={activeLegalDraft}
                            apiAddress={apiAddress}
                            chatId={currentChat}
                            onClose={closeLegalDraft}
                            onChatResponse={addLegalDraftChatResponse}
                            onUpdateArtifact={handleUpdateArtifact}
                            persistedState={legalDraftStates[activeLegalDraft.id]}
                            persistedStateLoading={legalDraftStatesLoading}
                            onPersistState={(state) => handlePersistLegalDraftState(activeLegalDraft.id, state)}
                            chatContext={legalDraftChatContext}
                            showChatSocReferences={sourceVisibilityByMessage[activeLegalDraft.messageIndex] ?? true}
                            onShowChatSocReferencesChange={(showSources) => handleSourceVisibilityChange(activeLegalDraft.messageIndex, showSources)}
                        />
                    </Box>
                )}
            </Flex>
            <UsersModal windowAddUser={windowAddUser} setWindowAddUser={setWindowAddUser} authorizedUsers={authorizedUsers} />
        </Flex>
    )
}
